import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Download, FileText, TrendingUp, Target, Archive } from "lucide-react";

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [period, setPeriod] = useState("monthly");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { api.get("/projects").then(r => { setProjects(r.data); if (r.data.length) setSelectedProject(r.data[0].id); }); }, []);
  useEffect(() => {
    if (!selectedProject) return;
    setLoading(true);
    api.get(`/reports/summary?project_id=${selectedProject}&period=${period}`).then(r => setSummary(r.data)).finally(() => setLoading(false));
  }, [selectedProject, period]);

  const download = async (kind) => {
    try {
      const path = kind === "uc" ? "/reports/utilization-certificate" : "/reports/pdf";
      const r = await api.get(`${path}?project_id=${selectedProject}&period=${period}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "uc" ? `UC-${period}.pdf` : `Report-${period}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind === "uc" ? "Utilization Certificate" : "Report"} downloaded`);
    } catch { toast.error("Failed"); }
  };

  const downloadSnapshot = async () => {
    setExporting(true);
    try {
      // Backend serves gzip when Accept-Encoding advertises it. Axios/fetch decode
      // transparently, so `r.data` here is the raw JSON — we save it with a
      // `.json` extension. Content-Length header will show the compressed size.
      const r = await api.get("/admin/export-snapshot", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `my-rkf-snapshot-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const sizeKb = Math.round(r.data.size / 1024);
      toast.success(`Snapshot downloaded (${sizeKb} KB) — keep it safe`);
    } catch (err) {
      toast.error(err.response?.status === 403
        ? "Only Admin can export the full snapshot"
        : "Snapshot download failed");
    } finally { setExporting(false); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Compliance</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Reports & Utilization Certificate</h1>
        <p className="text-sm text-slate-500 mt-1">Auto-generated monthly, quarterly and annual reports. Draft UC ready for signature.</p>
      </div>

      <Card className="mb-6">
        <CardContent className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div>
            <Label>Project</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger data-testid="report-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="report-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => download("report")} disabled={!selectedProject || !["Admin", "Accounts"].includes(user?.role)} className="flex-1 bg-[#0F172A]" data-testid="download-report-btn">
              <Download className="w-4 h-4 mr-1" /> Report PDF
            </Button>
            <Button onClick={() => download("uc")} disabled={!selectedProject || !["Admin", "Accounts"].includes(user?.role)} variant="outline" className="flex-1" data-testid="generate-uc-report-btn">
              <FileText className="w-4 h-4 mr-1" /> Draft UC
            </Button>
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card className="mb-6 border-slate-300 bg-slate-50/70" data-testid="snapshot-export-card">
          <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
              <Archive className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-base">Full data snapshot</div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Download a JSON copy of every collection — projects, budgets, reimbursements, advances, expenses,
                users, audit log, UCs and more. Useful as a safety net before onboarding or wiping demo data.
                Passwords are stripped; file is safe to keep locally.
              </p>
            </div>
            <Button
              onClick={downloadSnapshot}
              disabled={exporting}
              variant="outline"
              className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white whitespace-nowrap"
              data-testid="download-snapshot-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              {exporting ? "Preparing…" : "Download Snapshot"}
            </Button>
          </CardContent>
        </Card>
      )}

      {loading && <div className="text-slate-500 text-sm">Loading…</div>}
      {summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card><CardContent className="p-5">
              <div className="text-xs uppercase text-slate-500">Allocated</div>
              <div className="font-display font-bold text-xl mt-1">₹ {summary.totals.allocated.toLocaleString()}</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-xs uppercase text-slate-500">Paid in Period</div>
              <div className="font-display font-bold text-xl mt-1 text-emerald-600">₹ {summary.totals.paid.toLocaleString()}</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-xs uppercase text-slate-500">Utilization</div>
              <div className="font-display font-bold text-xl mt-1">{summary.totals.utilization_pct}%</div>
              <Progress value={summary.totals.utilization_pct} className="mt-2 h-1.5" />
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-xs uppercase text-slate-500">Milestones Done</div>
              <div className="font-display font-bold text-xl mt-1">{summary.milestones_summary.completed}/{summary.milestones_summary.total}</div>
            </CardContent></Card>
          </div>

          <Card>
            <CardContent className="p-5">
              <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Budget Head Utilization</h3>
              <div className="space-y-3">
                {summary.budget_heads.map(h => {
                  const pct = h.allocated ? Math.round((h.spent || 0) / h.allocated * 100) : 0;
                  return (
                    <div key={h.id}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{h.name}</span>
                        <span className="font-mono text-slate-500">₹{(h.spent||0).toLocaleString()} / ₹{h.allocated.toLocaleString()} ({pct}%)</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                    </div>
                  );
                })}
                {summary.budget_heads.length === 0 && <div className="text-sm text-slate-500">No budget heads yet.</div>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Target className="w-4 h-4" /> Expenses in Period ({summary.expenses.length})</h3>
              {summary.expenses.length === 0 ? (
                <div className="text-sm text-slate-500">No expenses recorded in this period.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500">
                      <tr className="border-b">
                        <th className="text-left py-2">Date</th>
                        <th className="text-left py-2">Title</th>
                        <th className="text-left py-2">Requester</th>
                        <th className="text-right py-2">Amount</th>
                        <th className="text-left py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.expenses.map(e => (
                        <tr key={e.id} className="border-b border-slate-100">
                          <td className="py-2 font-mono text-xs">{(e.created_at || "").slice(0,10)}</td>
                          <td className="py-2">{e.title}</td>
                          <td className="py-2">{e.requested_by_name}</td>
                          <td className="py-2 text-right font-mono">₹{(e.amount||0).toLocaleString()}</td>
                          <td className="py-2"><span className="status-chip bg-slate-100 text-slate-700">{e.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
